import { Box } from '@moonpig/launchpad-components'
import { useLoggedIn } from '@moonpig/web-core-auth'
import React, { FC } from 'react'
import { ModuleErrorBoundary } from '@moonpig/web-shared-products'
import { ContentModule } from '../../types'
import { TwoColumnLayout } from './TwoColumnLayout'

import {
  MODULE_CARD,
  MODULE_CTA_STRIP,
  MODULE_DELIVERY_STRIP,
  MODULE_BREADCRUMBS,
  MODULE_HEADING,
  MODULE_MISSION_BUTTONS,
  MODULE_PLACARDS,
  MODULE_RECOMMENDATIONS_FOR_CUSTOMER,
  MODULE_SEARCH_RESULTS,
  MODULE_TABBED_PRODUCT_LISTS,
  MODULE_USP_CAROUSEL,
  MODULE_PRODUCTS_LIST,
  MODULE_RECIPIENTS_LIST,
  MODULE_HERO,
  MODULE_CAROUSEL,
} from '../../modules/constants'
import { ModuleTracking } from '../../modules/types'
import { createTracking } from '../../analytics/tracking'
import { renderPageModule } from '../../modules/renderPageModule'

type RenderModulesParams = {
  modules: TrackedModule[]
  alternatingBackground: boolean
  eCardsCannibalizationTestVariation?: string
  loggedIn: boolean
}

type TrackedModule = ContentModule & {
  trackingData: TrackingData
}

type LandingProps = {
  modules: ContentModule[]
}

type TrackingData = {
  moduleIndex: number
  moduleType: string
}

const LIST_OF_MODULES_WITH_PRODUCT_LIST = [
  MODULE_SEARCH_RESULTS,
  MODULE_TABBED_PRODUCT_LISTS,
  MODULE_RECOMMENDATIONS_FOR_CUSTOMER,
]

const hasProductList = (module: ContentModule) => {
  return LIST_OF_MODULES_WITH_PRODUCT_LIST.includes(module.type)
}

const setProductListsTrackingData = (modules: TrackedModule[]) => {
  let productListIndexOnPage = 0
  const numberOfProductListsOnPage = modules.filter(hasProductList).length

  return modules.map(module => {
    if (hasProductList(module)) {
      productListIndexOnPage += 1

      return {
        ...module,
        tracking: {
          pageLocation: 'landing',
          moduleProductListIndex: productListIndexOnPage,
          moduleProductListsLength: numberOfProductListsOnPage,
        },
      }
    }

    return module
  })
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unnecessary-type-constraint
const partition = <T extends any>(
  arr: T[],
  predicate: (item: T) => boolean,
): [T[], T[]] =>
  arr.reduce<[T[], T[]]>(
    (acc, item) => {
      if (predicate(item)) {
        acc[0].push(item)
      } else {
        acc[1].push(item)
      }
      return acc
    },
    [[], []],
  )

const FIXED_BACKGROUND_MODULES = [
  MODULE_BREADCRUMBS,
  MODULE_HEADING,
  MODULE_HERO,
  MODULE_MISSION_BUTTONS,
  MODULE_RECIPIENTS_LIST,
  MODULE_DELIVERY_STRIP,
  MODULE_CTA_STRIP,
  MODULE_PRODUCTS_LIST,
  MODULE_USP_CAROUSEL,
  MODULE_CARD,
  MODULE_CAROUSEL,
]

const groupModules = (
  modules: TrackedModule[],
): {
  a: TrackedModule[]
  b: TrackedModule[]
  c: TrackedModule[]
  d: TrackedModule[]
} => {
  const heroModule = modules.find(module => module.type === MODULE_HERO)

  if (
    !heroModule ||
    (heroModule.type === MODULE_HERO && heroModule.heroVariant !== 'FullWidth')
  ) {
    return { a: modules, b: [], c: [], d: [] }
  }

  const heroModuleIndex = modules.indexOf(heroModule)
  const beforeHeroModules = modules.slice(0, heroModuleIndex)
  const afterHeroModules = modules.slice(heroModuleIndex + 1)
  const [besideHeroModules, belowHeroModules] = partition(
    afterHeroModules,
    module => module.type === MODULE_PLACARDS && module.items.length === 1,
  )

  return {
    a: beforeHeroModules,
    b: [heroModule],
    c: besideHeroModules,
    d: belowHeroModules,
  }
}

const renderModules = ({
  modules,
  alternatingBackground,
  loggedIn,
}: RenderModulesParams) => {
  let nextIndex = 0
  let numberOfProductCarouselsOnPage = 0
  let productCarouselIndexOnPage = 0

  const modulesWithProductListsTracking = setProductListsTrackingData(modules)

  return modulesWithProductListsTracking
    .reduce<TrackedModule[]>((accumulator, currentValue) => {
      return currentValue.type === MODULE_RECOMMENDATIONS_FOR_CUSTOMER &&
        !loggedIn
        ? accumulator
        : [...accumulator, currentValue]
    }, [])
    .map((module, index) => {
      let darkBackground = false

      if (!FIXED_BACKGROUND_MODULES.includes(module.type)) {
        // eslint-disable-next-line no-plusplus
        darkBackground = nextIndex++ % 2 === 0
      }

      if (hasProductList(module)) {
        const { tracking } = module as TrackedModule & ModuleTracking

        productCarouselIndexOnPage = tracking.moduleProductListIndex
        numberOfProductCarouselsOnPage = tracking.moduleProductListsLength
      }

      const page = 'landing'
      const backgroundColor =
        alternatingBackground && darkBackground
          ? 'colorBackgroundSite'
          : 'colorBackground01'
      const trackingData = {
        moduleIndex: index,
        moduleType: module.type,
        numberOfProductCarouselsOnPage,
        productCarouselIndexOnPage,
      }
      const context = {
        index,
        backgroundColor,
        customStyles: {
          pb: { xs: 8, lg: 8 },
          pt: { xs: 0, lg: 0 },
        },
        layout: page,
        tracking: createTracking({ page }),
      }

      return (
        // eslint-disable-next-line react/no-array-index-key
        <ModuleErrorBoundary key={index} type={module.type}>
          {renderPageModule({
            module,
            context,
            backgroundColor,
            page,
            trackingData: {
              ...trackingData,
            },
            ctaStripVariant: 'standard',
            deliveryStripVariant: 'standard',
          })}
        </ModuleErrorBoundary>
      )
    })
}

const Landing: FC<LandingProps> = ({ modules }) => {
  const { loggedIn } = useLoggedIn()

  const trackedModules = modules.map<TrackedModule>((module, index) => ({
    ...module,
    trackingData: {
      moduleIndex: index,
      moduleType: module.type,
    },
  }))

  const { a, b, c, d } = groupModules(trackedModules)
  const renderModulesParams = {
    alternatingBackground: false,
    loggedIn: loggedIn === true,
  }

  return (
    <Box bgcolor="white">
      {b.length > 0 ? (
        <TwoColumnLayout
          a={renderModules({
            modules: a,
            ...renderModulesParams,
          })}
          b={renderModules({
            modules: b,
            ...renderModulesParams,
          })}
          c={renderModules({
            modules: c,
            ...renderModulesParams,
          })}
          d={renderModules({
            modules: d,
            ...renderModulesParams,
            alternatingBackground: true,
          })}
        />
      ) : (
        <>
          {renderModules({
            modules: a,
            ...renderModulesParams,
            alternatingBackground: true,
          })}
        </>
      )}
    </Box>
  )
}

export { Landing }
